import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  schema,
}: SEOProps) => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://connectotransfers.com';

    if (title) {
      document.title = title;
    }

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.rel = 'canonical';
        document.head.appendChild(linkElement);
      }
      linkElement.href = `${siteUrl}${canonical}`;
    }

    if (ogTitle) {
      updateMetaTag('og:title', ogTitle, true);
    }

    if (ogDescription) {
      updateMetaTag('og:description', ogDescription, true);
    }

    if (ogImage) {
      updateMetaTag('og:image', `${siteUrl}${ogImage}`, true);
    }

    updateMetaTag('og:url', `${siteUrl}${canonical || '/'}`, true);

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Add or update Schema.org JSON-LD
    if (schema) {
      let scriptElement = document.querySelector('script[type="application/ld+json"]');
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.type = 'application/ld+json';
        document.head.appendChild(scriptElement);
      }
      scriptElement.textContent = JSON.stringify(schema);
    }

    return () => {
      // Cleanup if needed
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, schema]);
};

export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: 'Voyage Elite',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${siteUrl}/logo.png`,
      width: 250,
      height: 60
    },
    description: 'Service de transport privé premium entre Genève, Lyon et les stations de ski alpines. Excellence, ponctualité et confort depuis 2018.',
    foundingDate: '2018',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '775 Rue de Nogentil',
      addressLocality: 'Courchevel',
      postalCode: '73120',
      addressCountry: 'FR'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+33-6-05-60-60-06',
      contactType: 'customer service',
      email: 'contact@voyageelite.com',
      availableLanguage: ['French', 'English']
    },
    sameAs: [
      'https://www.instagram.com/qeistagram',
      'https://www.facebook.com/voyageelite',
      'https://www.linkedin.com/company/voyageelite'
    ]
  };
};

export const generateServiceSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${siteUrl}/#service`,
    serviceType: 'Transport Privé Premium',
    name: 'Transfert Privé Genève Lyon Stations de Ski',
    description: 'Service de transport privé premium avec chauffeur professionnel entre Genève, Lyon et les stations de ski alpines. Mercedes Classe E et Van luxueux, suivi des vols, service 24/7.',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Voyage Elite',
      url: siteUrl,
      telephone: '+33605606006'
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Genève'
      },
      {
        '@type': 'City',
        name: 'Lyon'
      },
      {
        '@type': 'Place',
        name: 'Courchevel'
      },
      {
        '@type': 'Place',
        name: 'Chamonix'
      },
      {
        '@type': 'Place',
        name: 'Megève'
      },
      {
        '@type': 'Place',
        name: 'Val d\'Isère'
      },
      {
        '@type': 'Place',
        name: 'Tignes'
      },
      {
        '@type': 'Place',
        name: 'Méribel'
      },
      {
        '@type': 'Place',
        name: 'Val Thorens'
      },
      {
        '@type': 'Place',
        name: 'Les Menuires'
      },
      {
        '@type': 'Place',
        name: 'La Plagne'
      },
      {
        '@type': 'Place',
        name: 'Les Arcs'
      },
      {
        '@type': 'Place',
        name: 'Alpe d\'Huez'
      },
      {
        '@type': 'Place',
        name: 'Les 2 Alpes'
      }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Services de Transfert',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Transfert Aéroport Genève - Stations de Ski',
            description: 'Transport privé depuis l\'aéroport de Genève vers les stations de ski alpines'
          },
          priceSpecification: {
            '@type': 'PriceSpecification',
            price: '180',
            priceCurrency: 'EUR',
            minPrice: '180',
            maxPrice: '320'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Transfert Lyon - Stations de Ski',
            description: 'Transport privé depuis Lyon vers les stations de ski alpines'
          },
          priceSpecification: {
            '@type': 'PriceSpecification',
            price: '280',
            priceCurrency: 'EUR',
            minPrice: '280',
            maxPrice: '290'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Transfert Genève - Lyon',
            description: 'Transport privé entre Genève et Lyon'
          },
          priceSpecification: {
            '@type': 'PriceSpecification',
            price: '220',
            priceCurrency: 'EUR'
          }
        }
      ]
    },
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '180',
      highPrice: '320',
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      url: `${siteUrl}/reservation`
    },
    availableChannel: {
      '@type': 'ServiceChannel',
      serviceUrl: `${siteUrl}/reservation`,
      servicePhone: '+33605606006',
      availableLanguage: ['French', 'English']
    }
  };
};

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://connectotransfers.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`
    }))
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};

export const generateProductSchema = (product: {
  name: string;
  description: string;
  price: string;
  image?: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://connectotransfers.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image ? `${siteUrl}${product.image}` : undefined,
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock'
    }
  };
};